import { useHistory, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useCapitalTransferContract, useMe } from '../../../common/apiHooks';
import { Loader } from '../../../common';
import { ContractDetails } from '../components/ContractDetails';
import { getContractDetailsPropsFromContract, getMyRole } from './utils';
import { BuyerFlow } from './buyer/BuyerFlow';
import { SellerConfirm } from './seller/SellerConfirm';
import { CapitalTransferContract } from '../../../common/apiModels/capital-transfer';
import { usePageTitle } from '../../../common/usePageTitle';

export const CapitalTransferStatus = () => {
  usePageTitle('pageTitle.capitalNewApplication');

  const params = useParams<{ id: string }>();

  const { data: me } = useMe();

  const [manualFetching, setManualFetching] = useState(false);
  const {
    data: contract,
    error,
    refetch,
  } = useCapitalTransferContract(Number(params.id), manualFetching);

  if (error) {
    if (error.status === 404) {
      return (
        <div className="alert alert-warning">
          <FormattedMessage id="capital.transfer.notFound" />
        </div>
      );
    }

    return (
      <div className="alert alert-warning">
        <FormattedMessage id="capital.transfer.otherError" />
      </div>
    );
  }

  if (!me || !contract) {
    return <Loader className="align-middle" />;
  }

  const myRole = getMyRole(me, contract);

  const showBuyerFlow =
    (contract.state === 'SELLER_SIGNED' || contract.state === 'BUYER_SIGNED') && myRole === 'BUYER';

  const showSellerConfirmation =
    contract.state === 'PAYMENT_CONFIRMED_BY_BUYER' && myRole === 'SELLER';

  return (
    <div className="col-12 col-md-10 col-lg-7 mx-auto d-flex flex-column gap-5">
      {showBuyerFlow && (
        <BuyerFlow
          contract={contract}
          onRefetch={() => refetch()}
          onShown={() => setManualFetching(true)}
        />
      )}
      {showSellerConfirmation && (
        <SellerConfirm
          contract={contract}
          onConfirmed={() => refetch()}
          onShown={() => setManualFetching(true)}
        />
      )}
      {!showBuyerFlow && !showSellerConfirmation && (
        <StatusDisplay contract={contract} onShown={() => setManualFetching(false)} />
      )}
    </div>
  );
};

const StatusDisplay = ({
  contract,
  onShown,
}: {
  contract: CapitalTransferContract;
  onShown: () => unknown;
}) => {
  const { data: me } = useMe();
  const history = useHistory();

  useEffect(() => {
    onShown();
  }, []);

  if (!me) {
    return <Loader className="align-middle" />;
  }

  return (
    <>
      <div className="d-flex flex-column gap-4">
        <h1 className="m-0 text-md-center">
          <FormattedMessage id="capital.transfer.details.heading" />
        </h1>
      </div>

      {contract.state === 'CANCELLED' && (
        <div className="alert alert-warning m-0" role="alert">
          <FormattedMessage id="capital.transfer.details.state.cancelled" />
        </div>
      )}

      {contract.state === 'PAYMENT_CONFIRMED_BY_SELLER' && (
        <div className="alert alert-primary d-flex align-items-top gap-2 m-0" role="alert">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            fill="currentColor"
            viewBox="0 0 16 16"
            className="flex-shrink-0 mt-1"
            aria-hidden="true"
          >
            <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14m0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16" />
            <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0" />
          </svg>
          <FormattedMessage id="capital.transfer.details.state.sentToBoard" />
        </div>
      )}

      {['APPROVED', 'EXECUTED', 'APPROVED_AND_NOTIFIED'].includes(contract.state) && (
        <div className="alert alert-success m-0" role="alert">
          <FormattedMessage id="capital.transfer.details.state.approved" />
        </div>
      )}

      <div className="py-4">
        <ContractDetails
          {...getContractDetailsPropsFromContract(contract)}
          userRole={getMyRole(me, contract)}
        />
      </div>

      <div className="d-flex flex-column-reverse flex-sm-row justify-content-between pt-4 border-top gap-3">
        <button
          type="button"
          className="btn btn-lg btn-light"
          onClick={() => history.push('/capital/listings/')}
        >
          <FormattedMessage id="capital.transfer.details.button.back" />
        </button>
      </div>
    </>
  );
};
